import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import Loader from '../../themed/loader';
import Progress from '../../themed/progress';
import Header from '../../blocks/header';
import UpdateUser from '../../forms/updateUser';
import Status from '../../blocks/status';

import * as models from '../../../utils/models';
import * as service from '../../../services/userService';
import * as toastService from '../../../services/toastService';
import * as utils from '../../../utils/utilities';

import useProgress from '../../../hooks/useProgress';
import withRouter from '../../hoc/withRouter';

import classNames from './style.module.scss';

const UserDetails: React.FC = (props: any) => {
	const [user, setUser] = useState<models.User | null>();
	const [loading, setLoading] = useState<boolean>(false);

	const { progress, onProgress } = useProgress();
	const { userId } = useParams();

	const deactivate = async (): Promise<void> => {
		if (!user) return;

		await service.deactivateUser(user.id, onProgress);
	};

	const resendWelcomeEmail = (): void => {
		if (user && user.email) {
			toastService.promise(service.resendWelcomeEmail(user!.email), {
				success: `Email Resent Successfully!`,
				loading: 'Resending Welcome Email',
				error: utils.getErrorMessage,
			});
		}
	};

	const handleUserDeactivation = () => {
		toastService.promise(deactivate(), {
			success: `${user?.firstName} deactivated successfully`,
			loading: 'Deactivating User',
			error: utils.getErrorMessage,
		});
	};

	const getUser = async (): Promise<void> => {
		if (!userId) return;

		try {
			setLoading(true);

			const result = await service.getUser(userId);
			setUser(result.data);

			setLoading(false);
		} catch (error: any) {
			const errorMessage: string = utils.getErrorMessage(error);
			toastService.error(errorMessage);

			setLoading(false);
		}
	};

	useEffect(() => {
		getUser();

		// eslint-disable-next-line
	}, [userId]);

	return (
		<React.Fragment>
			<Progress progress={progress} />

			<Header
				subtitle={
					'Showing Details for User: ' + (user ? user.firstName : 'N/A')
				}
			/>

			<div className={classNames.users}>
				<Loader isVisible={loading} />

				<Conditional isVisible={!user}>
					<div className="bg-white border d-flex justify-content-center pb-4">
						<Button label="Refresh" icon="arrow-clockwise" onClick={getUser} />
					</div>
				</Conditional>

				<Conditional isVisible={!!user}>
					<React.Fragment>
						<div className="d-flex justify-content-start align-items-center">
							<div className={classNames.userDetailsIcon}>
								<i className="bi bi-person-bounding-box"></i>
							</div>

							<div className={classNames.userDetailsBody}>
								<h3>
									{user?.firstName} {user?.lastName}
								</h3>
								<div className={classNames.userDetailsBodyAddress}>
									{user?.designation}
								</div>
								<div className={classNames.userDetailsBodySubdetails}>
									<span className={classNames.phone}>
										{user?.phone} | {user?.email}
									</span>
								</div>
							</div>

							<div className={classNames.userDetailsRole}>{user?.roleName}</div>
						</div>

						<div className="d-flex justify-content-end align-items-center mt-3">
							<Status isActive={user?.isActive as boolean} />

							<Conditional isVisible={!user?.isActive as boolean}>
								<button
									disabled={loading}
									className={classNames.userActionButton}
									onClick={resendWelcomeEmail}
								>
									<i className="bi bi-envelope"></i>
									Send Activation Email
								</button>
							</Conditional>


							<Conditional isVisible={user?.isActive as boolean}>
								<button
									disabled={loading}
									className={classNames.userActionButton}
									onClick={handleUserDeactivation}
								>
									<i className="bi bi-slash-circle"></i>
									Deactivate
								</button>
							</Conditional>
						</div>

						<div className={classNames.usersDetails}>
							<UpdateUser user={user} />
						</div>
					</React.Fragment>
				</Conditional>
			</div>
		</React.Fragment>
	);
};

export default withRouter(UserDetails);
